import { PlaycastGamepadDevice, PlaycastKeyDetail, PlaycastMouseButton } from '@playcastdotio/CoreTypes';
import { captureStore } from '@playcastdotio/InputCapture';
import { generators, PlaycastMessage, PlaycastMessageTarget } from '@playcastdotio/Messaging';
import { MobileGamepadState, mobileGamepadToGamepadStateMessage } from '@playcastdotio/MobileGamepad';
import { sendPeerMessage, getPlayerPeerId, sampleInputForPhoton } from '../peer';
import { INPUT_PHOTON_SEQ_METADATA_KEY } from '@playcastdotio/PeerCore';
import { playerGamepadState } from '../../stores/playerStores';
import { playerConnectionState } from '../../stores/playerStores';
import { convertGamepads, getKeyboardKeyLocation, getMouseButton, isMobileDevice } from '../../../utility/utility';
import { isPavilionMode, getSelectedGamepadIndex, setSelectedGamepadIndex } from '../../../utility/gamepadSelection';
import {
  captureInputTimestamp,
  captureMessageTimestamp,
  resetTiming,
  getInputToMessageLatency,
  inputTimestamp,
  messageTimestamp,
} from './timingUtils';
import { browserTouchEventToPlaycastTouchEvent } from './touchInputHelpers';

// Track previous mouse position for delta calculation
let previousMousePosition = { x: 0, y: 0 };

// Optional element reference for computing element-relative touch coordinates (0–1).
// Set via setTouchTargetElement() — typically the game/video container element.
let touchTargetElement: HTMLElement | null = null;

// Active CSS rotation of the touch target in degrees (0, 90, or -90). Set via
// setTouchTargetRotation() from the player when the video is force-rotated in
// mobile fullscreen, so touch coordinates map through the un-rotated local frame.
let touchTargetRotationDeg = 0;

/**
 * Set the element that touch coordinates should be relative to.
 * When set, `relativeX`/`relativeY` in PlaycastTouch will be
 * normalised 0–1 values within this element's bounding box.
 */
export const setTouchTargetElement = (element: HTMLElement | null) => {
  touchTargetElement = element;
};

/**
 * Set the touch target's active CSS rotation (0, 90, or -90 degrees).
 * Used to invert the force-landscape rotation so fullscreen touches don't all
 * map to the bottom-right corner.
 */
export const setTouchTargetRotation = (rotationDeg: number) => {
  touchTargetRotationDeg = rotationDeg;
};

/**
 * Get the touch target element dynamically.
 * Falls back to finding the video element if the cached reference is null.
 * This works around React StrictMode and timing issues.
 */
const getTouchTargetElement = (): HTMLElement | null => {
  if (touchTargetElement) {
    return touchTargetElement;
  }

  const videoElement = document.querySelector('video');
  if (videoElement) {
    const rect = videoElement.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) {
      return videoElement as HTMLElement;
    }
  }

  const bodyRect = document.body.getBoundingClientRect();
  if (bodyRect.width > 0 && bodyRect.height > 0) {
    return document.body;
  }

  return null;
};

// Track gamepad button states for first-input detection
const lastGamepadButtonStates: Map<number, boolean[]> = new Map();
let firstInputDetected = false;

// Debug callback for message interception (for debug overlay)
let debugMessageCallback: ((message: PlaycastMessage<PlaycastMessageTarget>) => void) | null = null;

export const setDebugMessageCallback = (callback: ((message: PlaycastMessage<PlaycastMessageTarget>) => void) | null) => {
  debugMessageCallback = callback;
};

/**
 * Inputs eligible for input-to-photon sampling, by target and action.
 *
 * Every forwarded input is a candidate, including continuous motion (mouse move, wheel,
 * touch move, gamepad state). The quantity measured is the same for all of them — the
 * time from the input event until the first frame the host encoded after injecting it —
 * so a player wiggling a mouse or holding a stick produces the same latency reading as a
 * key press, and a session with no discrete presses still gets measured.
 *
 * Volume is not a concern: the tracker hands out at most one sequence per
 * INPUT_PHOTON_SAMPLE_INTERVAL_MS, so a 120 Hz pointermove stream is throttled to the same
 * ~2 tags/sec as anything else before a byte reaches the channel.
 */
const PHOTON_SAMPLED_INPUTS: Readonly<Record<string, readonly string[]>> = {
  keyboard: ['down', 'up'],
  mouse: ['down', 'up', 'move', 'wheel'],
  touch: ['Start', 'End', 'move'],
  gamepad: ['setState'],
};

/**
 * Tag a discrete input so the host can name the frame that answers it.
 *
 * Applied here rather than at the eleven per-event call sites: they all funnel through
 * `send`, and a rule duplicated eleven times is a rule that drifts. Returns the message
 * unchanged when the input is not sampled, which is the overwhelming majority.
 */
const stampInputPhotonSeq = (
  message: PlaycastMessage<PlaycastMessageTarget>,
): PlaycastMessage<PlaycastMessageTarget> => {
  // Optional chaining is deliberate: this is a debug metric sitting on the path that
  // delivers every keystroke and tap. A message shaped unexpectedly must cost us the
  // measurement, never the input.
  const eligible = PHOTON_SAMPLED_INPUTS[message.header?.target];
  if (!eligible?.includes(message.header?.action as string)) return message;

  const inputSeq = sampleInputForPhoton();
  if (inputSeq === null) return message;

  return generators.addTiming(
    message,
    { timestamp: performance.now(), description: 'input_photon_sample' },
    'player',
    { [INPUT_PHOTON_SEQ_METADATA_KEY]: inputSeq.toString() },
  );
};

const send = (message: PlaycastMessage<PlaycastMessageTarget>) => {
  if (debugMessageCallback) {
    debugMessageCallback(message);
  }

  const isConnected = playerConnectionState.currentState.status === 'connected';
  if (isConnected) {
    sendPeerMessage(stampInputPhotonSeq(message));
  }
};

let latestGamepadState: PlaycastGamepadDevice[] = [];

// Whether a non-neutral mobile gamepad state has been forwarded since the last
// neutral flush. Lets `setGamepadState` send exactly one release/neutral update
// when gamepad access is revoked, so the host doesn't hold a stuck last state.
let mobileGamepadForwarded = false;

// Custom comparison function that ignores timestamps (which change constantly)
const gamepadStatesEqual = (prev: PlaycastGamepadDevice[], current: PlaycastGamepadDevice[]): boolean => {
  if (prev.length !== current.length) return false;

  for (let i = 0; i < prev.length; i++) {
    const p = prev[i];
    const c = current[i];

    // Compare everything EXCEPT timestamp
    if (
      p.deviceId !== c.deviceId ||
      p.id !== c.id ||
      p.xInput.wButtons !== c.xInput.wButtons ||
      p.xInput.bLeftTrigger !== c.xInput.bLeftTrigger ||
      p.xInput.bRightTrigger !== c.xInput.bRightTrigger ||
      p.xInput.sThumbLX !== c.xInput.sThumbLX ||
      p.xInput.sThumbLY !== c.xInput.sThumbLY ||
      p.xInput.sThumbRX !== c.xInput.sThumbRX ||
      p.xInput.sThumbRY !== c.xInput.sThumbRY
    ) {
      return false;
    }
  }

  return true;
};

// Function to detect first gamepad input and auto-select
const detectFirstGamepadInput = (capture: (Gamepad | null)[]): number | null => {
  if (firstInputDetected) {
    return null; // Already detected first input
  }

  for (let i = 0; i < capture.length; i++) {
    const gamepad = capture[i];
    if (!gamepad || !gamepad.connected) continue;

    const currentButtonStates = gamepad.buttons.map((button) => button.pressed);
    const lastButtonStates = lastGamepadButtonStates.get(i) || [];

    // Check for any button press or significant stick movement
    for (let j = 0; j < currentButtonStates.length; j++) {
      if (currentButtonStates[j] && !lastButtonStates[j]) {
        // Button press detected - this is the first input
        firstInputDetected = true;
        setSelectedGamepadIndex(i);
        return i;
      }
    }

    // Check for significant stick movement (threshold to avoid noise)
    const leftStickMoved = Math.abs(gamepad.axes[0]) > 0.1 || Math.abs(gamepad.axes[1]) > 0.1;
    const rightStickMoved = Math.abs(gamepad.axes[2]) > 0.1 || Math.abs(gamepad.axes[3]) > 0.1;

    if (leftStickMoved || rightStickMoved) {
      // Stick movement detected - this is the first input
      firstInputDetected = true;
      setSelectedGamepadIndex(i);
      return i;
    }

    // Update button states for next comparison
    lastGamepadButtonStates.set(i, currentButtonStates);
  }

  return null;
};

export const handleEvent = (event: Event) => {
  const isConnected = playerConnectionState.currentState.status === 'connected';
  const peerId: string = isConnected ? getPlayerPeerId() : 'debug-mode';

  // In debug mode (when callback is registered but not connected), allow message generation
  // This lets us test the input pipeline without a peer connection
  const allowDebugMode = !isConnected && debugMessageCallback !== null;

  if (!isConnected && !allowDebugMode) {
    // Not connected and not in debug mode - skip processing
    return;
  }

  if (isConnected && !peerId) {
    console.error('Peer ID is not defined');
    return;
  }

  // Defense-in-depth: even if touch listeners are momentarily still bound, never
  // forward touch to a connected host that has not granted touch access. The
  // authoritative access lives in `captureStore` (fed by `setInputAccess`).
  // Debug mode (not connected) is exempt so the input harness still works.
  const isTouchEvent =
    event.type === 'touchstart' ||
    event.type === 'touchend' ||
    event.type === 'touchmove' ||
    event.type === 'touchcancel';
  if (isConnected && isTouchEvent && !captureStore.getAccess().touch) {
    return;
  }

  // Capture timing when user input occurs
  captureInputTimestamp();

  switch (event.type) {
    case 'gamepad': {
        // Defense-in-depth: if the host has not granted gamepad access, stop
        // forwarding polled/physical gamepad input. If we previously sent state,
        // flush one neutral (empty) update first so the host doesn't hold the
        // last-pressed state. Debug mode (not connected) is exempt.
        if (isConnected && !captureStore.getAccess().gamepad) {
          if (latestGamepadState.length > 0) {
            latestGamepadState = [];
            send(
              generators.gamepad.setState(peerId, {
                gamepads: [],
                playerCoordinates: playerGamepadState.currentState.playerCoordinates,
              }),
            );
          }
          break;
        }

        const e = event as CustomEvent;
        const capture = e.detail as (Gamepad | null)[];

        let connectedGamepads: Gamepad[] = [];

        // PAVILION MODE ONLY: Filter gamepad input based on user selection
        // In Prime mode, host controls access - allow all gamepads if access granted
        if (isPavilionMode()) {
          const selectedSlotIndex = getSelectedGamepadIndex();

          // If no selection made yet, try to detect first input
          if (selectedSlotIndex === null && !firstInputDetected) {
            const firstInputSlot = detectFirstGamepadInput(capture);
            if (firstInputSlot !== null) {
              // First input detected and gamepad selected - now filter for that one
              const selectedGamepad = capture[firstInputSlot];
              if (selectedGamepad && selectedGamepad.connected) {
                connectedGamepads = [selectedGamepad];
              } else {
                connectedGamepads = [];
              }
            } else {
              // No first input yet - allow all gamepads until one is selected
              connectedGamepads = capture.filter((x) => x) as Gamepad[];
            }
          } else if (selectedSlotIndex === -2) {
            // Spectator selected - disable all gamepad input
            connectedGamepads = [];
          } else if (selectedSlotIndex === -1) {
            // Mouse/Keyboard selected - disable all gamepad input
            connectedGamepads = [];
          } else if (selectedSlotIndex !== null && selectedSlotIndex >= 0) {
            // Specific gamepad slot selected - find it in the original capture array
            const selectedGamepad = capture[selectedSlotIndex];
            if (selectedGamepad && selectedGamepad.connected) {
              // Only keep the selected gamepad
              connectedGamepads = [selectedGamepad];
            } else {
              // Selected gamepad not available at that slot - disable all gamepad input
              connectedGamepads = [];
            }
          } else {
            // No selection made - disable all gamepad input
            connectedGamepads = [];
          }
        } else {
          // PRIME MODE: Use all connected gamepads - host controls access permissions
          connectedGamepads = capture.filter((x) => x) as Gamepad[];
        }

        // CRITICAL FIX: Only process and send the filtered gamepads
        // This ensures that blocked gamepads don't get through to the host
        if (connectedGamepads.length > 0) {
          const filteredGamepadEvents = convertGamepads(connectedGamepads);

          if (!gamepadStatesEqual(latestGamepadState, filteredGamepadEvents)) {
            latestGamepadState = filteredGamepadEvents;

            const message = generators.gamepad.setState(peerId, {
              gamepads: filteredGamepadEvents,
              playerCoordinates: playerGamepadState.currentState.playerCoordinates,
            });

            captureMessageTimestamp();
            const messageWithTiming = generators.addTiming(
              message,
              {
                timestamp: performance.now(),
                description: 'input_to_message_latency',
              },
              'player',
              {
                input_timestamp: inputTimestamp.toString(),
                message_timestamp: messageTimestamp.toString(),
                latency_ms: getInputToMessageLatency().toString(),
              },
            );

            send(messageWithTiming);

            resetTiming();
          }
        } else {
          // No gamepads allowed - send empty state to clear any previous input
          if (latestGamepadState.length > 0) {
            latestGamepadState = [];

            const message = generators.gamepad.setState(peerId, {
              gamepads: [],
              playerCoordinates: playerGamepadState.currentState.playerCoordinates,
            });

            captureMessageTimestamp();
            const messageWithTiming = generators.addTiming(
              message,
              {
                timestamp: performance.now(),
                description: 'input_to_message_latency',
              },
              'player',
              {
                input_timestamp: inputTimestamp.toString(),
                message_timestamp: messageTimestamp.toString(),
                latency_ms: getInputToMessageLatency().toString(),
              },
            );

            send(messageWithTiming);

            resetTiming();
          }
        }
        break;
      }

      case 'keydown': {
        const e = event as KeyboardEvent;

        const detail: PlaycastKeyDetail = {
          keyCode: e.which,
          location: getKeyboardKeyLocation(e.location),
          source: {
            type: 'browser',
            code: e.code,
          },
          modifiers: {
            alt: e.altKey,
            ctrl: e.ctrlKey,
            meta: e.metaKey,
            shift: e.shiftKey,
          },
          displayName: e.key,
        };

        const message = generators.keyboard.down(peerId, detail);

        captureMessageTimestamp();
        const messageWithTiming = generators.addTiming(
          message,
          {
            timestamp: performance.now(),
            description: 'input_to_message_latency',
          },
          'player',
          {
            input_timestamp: inputTimestamp.toString(),
            message_timestamp: messageTimestamp.toString(),
            latency_ms: getInputToMessageLatency().toString(),
          },
        );

        send(messageWithTiming);

        resetTiming();
        break;
      }

      case 'keyup': {
        const e = event as KeyboardEvent;

        const detail: PlaycastKeyDetail = {
          keyCode: e.which,
          location: getKeyboardKeyLocation(e.location),
          source: {
            type: 'browser',
            code: e.code,
          },
          modifiers: {
            alt: e.altKey,
            ctrl: e.ctrlKey,
            meta: e.metaKey,
            shift: e.shiftKey,
          },
          displayName: e.key,
        };

        const message = generators.keyboard.up(peerId, detail);

        captureMessageTimestamp();
        const messageWithTiming = generators.addTiming(
          message,
          {
            timestamp: performance.now(),
            description: 'input_to_message_latency',
          },
          'player',
          {
            input_timestamp: inputTimestamp.toString(),
            message_timestamp: messageTimestamp.toString(),
            latency_ms: getInputToMessageLatency().toString(),
          },
        );

        send(messageWithTiming);

        resetTiming();
        break;
      }

      case 'pointerup': {
        const e = event as MouseEvent;

        const detail: PlaycastMouseButton = {
          button: getMouseButton(e.button),
          clickCount: 1,
          delta: {
            x: e.movementX,
            y: e.movementY,
          },
          playerCoordinates: {
            origin: 'topLeft',
            dimensions: {
              x: e.clientX,
              y: e.clientY,
            },
            display: {
              x: e.screenX,
              y: e.screenY,
            },
          },
          position: {
            x: 0,
            y: 0,
          },
        };

        const message = generators.mouse.up(peerId, detail);

        captureMessageTimestamp();
        const messageWithTiming = generators.addTiming(
          message,
          {
            timestamp: performance.now(),
            description: 'input_to_message_latency',
          },
          'player',
          {
            input_timestamp: inputTimestamp.toString(),
            message_timestamp: messageTimestamp.toString(),
            latency_ms: getInputToMessageLatency().toString(),
          },
        );

        send(messageWithTiming);

        resetTiming();
        break;
      }

      case 'pointerdown': {
        const e = event as MouseEvent;

        const detail: PlaycastMouseButton = {
          button: getMouseButton(e.button),
          clickCount: 1,
          delta: {
            x: e.movementX,
            y: e.movementY,
          },
          playerCoordinates: {
            origin: 'topLeft',
            dimensions: {
              x: e.clientX,
              y: e.clientY,
            },
            display: {
              x: e.screenX,
              y: e.screenY,
            },
          },
          position: {
            x: 0,
            y: 0,
          },
        };

        const message = generators.mouse.down(peerId, detail);

        captureMessageTimestamp();
        const messageWithTiming = generators.addTiming(
          message,
          {
            timestamp: performance.now(),
            description: 'input_to_message_latency',
          },
          'player',
          {
            input_timestamp: inputTimestamp.toString(),
            message_timestamp: messageTimestamp.toString(),
            latency_ms: getInputToMessageLatency().toString(),
          },
        );

        send(messageWithTiming);

        resetTiming();
        break;
      }

      case 'pointermove': {
        const e = event as MouseEvent;

        // Try to get delta from movementX/Y first
        let deltaX = e.movementX;
        let deltaY = e.movementY;

        // If movementX/Y are zero or undefined, try calculating from client coordinates
        if (deltaX === 0 && deltaY === 0) {
          deltaX = e.clientX - previousMousePosition.x;
          deltaY = e.clientY - previousMousePosition.y;
        }

        // Update previous position using client coordinates
        previousMousePosition = { x: e.clientX, y: e.clientY };

        // Only send if we have non-zero deltas
        if (deltaX !== 0 || deltaY !== 0) {
          const message = generators.mouse.move(peerId, {
            delta: {
              x: deltaX,
              y: deltaY,
            },
            playerCoordinates: {
              origin: 'topLeft',
              dimensions: {
                x: e.clientX,
                y: e.clientY,
              },
              display: {
                x: e.screenX,
                y: e.screenY,
              },
            },
            position: {
              x: 0,
              y: 0,
            },
          });

          captureMessageTimestamp();
          const messageWithTiming = generators.addTiming(
            message,
            {
              timestamp: performance.now(),
              description: 'input_to_message_latency',
            },
            'player',
            {
              input_timestamp: inputTimestamp.toString(),
              message_timestamp: messageTimestamp.toString(),
              latency_ms: getInputToMessageLatency().toString(),
            },
          );

          send(messageWithTiming);

          resetTiming();
        }
        break;
      }

      case 'wheel': {
        const e = event as WheelEvent;

        const message = generators.mouse.wheel(peerId, {
          playerCoordinates: {
            origin: 'topLeft',
            dimensions: {
              x: e.clientX,
              y: e.clientY,
            },
            display: {
              x: e.screenX,
              y: e.screenY,
            },
          },
          position: {
            x: 0,
            y: 0,
          },
          scroll: {
            x: e.deltaX,
            y: -e.deltaY,
          },
          mode: e.deltaMode,
        });

        captureMessageTimestamp();
        const messageWithTiming = generators.addTiming(
          message,
          {
            timestamp: performance.now(),
            description: 'input_to_message_latency',
          },
          'player',
          {
            input_timestamp: inputTimestamp.toString(),
            message_timestamp: messageTimestamp.toString(),
            latency_ms: getInputToMessageLatency().toString(),
          },
        );

        send(messageWithTiming);

        resetTiming();
        break;
      }

      case 'touchstart': {
        const e = event as TouchEvent;
        const targetElement = getTouchTargetElement();
        const touchEvent = browserTouchEventToPlaycastTouchEvent(e, targetElement, touchTargetRotationDeg);
        const message = generators.touch.start(peerId, touchEvent);

        captureMessageTimestamp();
        const messageWithTiming = generators.addTiming(
          message,
          {
            timestamp: performance.now(),
            description: 'input_to_message_latency',
          },
          'player',
          {
            input_timestamp: inputTimestamp.toString(),
            message_timestamp: messageTimestamp.toString(),
            latency_ms: getInputToMessageLatency().toString(),
          },
        );

        send(messageWithTiming);

        resetTiming();
        break;
      }

      case 'touchend': {
        const e = event as TouchEvent;
        const targetElement = getTouchTargetElement();
        const touchEvent = browserTouchEventToPlaycastTouchEvent(e, targetElement, touchTargetRotationDeg);
        const message = generators.touch.end(peerId, touchEvent);

        captureMessageTimestamp();
        const messageWithTiming = generators.addTiming(
          message,
          {
            timestamp: performance.now(),
            description: 'input_to_message_latency',
          },
          'player',
          {
            input_timestamp: inputTimestamp.toString(),
            message_timestamp: messageTimestamp.toString(),
            latency_ms: getInputToMessageLatency().toString(),
          },
        );

        send(messageWithTiming);

        resetTiming();
        break;
      }

      case 'touchmove': {
        const e = event as TouchEvent;
        const targetElement = getTouchTargetElement();
        const touchEvent = browserTouchEventToPlaycastTouchEvent(e, targetElement, touchTargetRotationDeg);
        const message = generators.touch.move(peerId, touchEvent);

        captureMessageTimestamp();
        const messageWithTiming = generators.addTiming(
          message,
          {
            timestamp: performance.now(),
            description: 'input_to_message_latency',
          },
          'player',
          {
            input_timestamp: inputTimestamp.toString(),
            message_timestamp: messageTimestamp.toString(),
            latency_ms: getInputToMessageLatency().toString(),
          },
        );

        send(messageWithTiming);

        resetTiming();
        break;
      }

      case 'touchcancel': {
        const e = event as TouchEvent;
        // Get touch target element dynamically (fixes React StrictMode issues)
        const targetElement = getTouchTargetElement();
        const touchEvent = browserTouchEventToPlaycastTouchEvent(e, targetElement, touchTargetRotationDeg);
        const message = generators.touch.cancel(peerId, touchEvent);

        captureMessageTimestamp();
        const messageWithTiming = generators.addTiming(
          message,
          {
            timestamp: performance.now(),
            description: 'input_to_message_latency',
          },
          'player',
          {
            input_timestamp: inputTimestamp.toString(),
            message_timestamp: messageTimestamp.toString(),
            latency_ms: getInputToMessageLatency().toString(),
          },
        );

        send(messageWithTiming);

        resetTiming();
        break;
      }
  }
};

export const setGamepadState = (gamepadState: MobileGamepadState) => {
  const isConnected = playerConnectionState.currentState.status === 'connected';
  const peerId: string = isConnected ? (playerConnectionState.currentState.playerPeerId || '') : 'debug-mode';

  // Allow debug mode when callback is registered
  const allowDebugMode = !isConnected && debugMessageCallback !== null;

  if (!isConnected && !allowDebugMode) {
    // Not connected and not in debug mode - skip processing
    return;
  }

  // Defense-in-depth: don't forward mobile-gamepad input to a connected host that
  // has not granted gamepad access (the overlay should be unmounted, but this
  // guards against a stray send). If we previously forwarded input, flush one
  // neutral state so the host doesn't hold the last-pressed state, then suppress.
  // Debug mode is exempt.
  if (isConnected && !captureStore.getAccess().gamepad) {
    if (mobileGamepadForwarded) {
      mobileGamepadForwarded = false;
      send(
        generators.gamepad.setState(peerId, {
          gamepads: [],
          playerCoordinates: playerGamepadState.currentState.playerCoordinates,
        }),
      );
    }
    return;
  }

  // On mobile devices, never block mobile gamepad input based on slot selection
  // Mobile devices don't have physical gamepad slots like desktop
  if (isMobileDevice()) {
    mobileGamepadForwarded = true;
    send(generators.gamepad.setState(peerId, mobileGamepadToGamepadStateMessage(gamepadState)));
    return;
  }

  // DESKTOP PAVILION MODE: Apply slot-based filtering logic
  if (isPavilionMode()) {
    const selectedSlotIndex = getSelectedGamepadIndex();

    // If spectator, mouse/keyboard, or no selection made - block mobile gamepad input
    if (selectedSlotIndex === -2 || selectedSlotIndex === -1 || selectedSlotIndex === null) {
      return;
    }

    // If physical gamepad slot selected (0-3), check if there's actually a gamepad connected
    if (selectedSlotIndex >= 0) {
      // Check if there's actually a physical gamepad connected at the selected slot
      const allGamepads = navigator.getGamepads?.() || [];
      const selectedGamepad = allGamepads[selectedSlotIndex];

      // Only block mobile gamepad if there's an actual physical gamepad connected at the slot
      if (selectedGamepad && selectedGamepad.connected) {
        return; // Physical gamepad is connected, block mobile gamepad
      }
      // If no physical gamepad is connected at the selected slot, allow mobile gamepad to work
    }
  }

  mobileGamepadForwarded = true;
  send(generators.gamepad.setState(peerId, mobileGamepadToGamepadStateMessage(gamepadState)));
};

// Function to reset first-input detection for new sessions
export const resetFirstInputDetection = () => {
  firstInputDetected = false;
  lastGamepadButtonStates.clear();
  latestGamepadState = [];
  mobileGamepadForwarded = false;
};
